import { useState } from 'react';
import { Alert, Button, DialogTitle, FormControl, FormLabel, Input, Modal, ModalDialog, Stack, Textarea, Typography } from '@mui/joy';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StaffPicker } from '../../../components/admin/StaffPicker';
import { AdminOnboardingAPI } from '../../../lib/apiAdmin';
import { getApiErrorMessage } from '../../../lib/apiError';
import type { StaffTask } from '../../../types/admin';

function localDateTime(value: string | null) {
  if (!value) return '';
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}
export function DashboardTaskDialog({ task, actorId, onClose }: { task?: StaffTask; actorId: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(task?.title ?? '');
  const [notes, setNotes] = useState(task?.notes ?? '');
  const [due, setDue] = useState(localDateTime(task?.due_at ?? null));
  const [assignee, setAssignee] = useState<number | null>(task ? task.assignee : actorId);
  const save = useMutation({
    mutationFn: () => {
      const values = { title: title.trim(), notes, assignee, due_at: due ? new Date(due).toISOString() : null };
      return task ? AdminOnboardingAPI.updateTask(task.id, values) : AdminOnboardingAPI.createTask(values);
    },
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['admin'] }); onClose(); },
  });
  return <Modal open onClose={() => { if (!save.isPending) onClose(); }}>
    <ModalDialog sx={{ width: 'min(520px, calc(100vw - 24px))', maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto' }}>
      <DialogTitle>{task ? 'Edit task' : 'Create task'}</DialogTitle>
      <Typography level="body-sm">Keep the next step clear and give it an owner.</Typography>
      <form onSubmit={event => { event.preventDefault(); if (title.trim() && !save.isPending) save.mutate(); }}>
        <Stack spacing={2}>
          {save.error && <Alert color="danger">{getApiErrorMessage(save.error, 'Task could not be saved. Your changes are still here.')}</Alert>}
          <FormControl required><FormLabel>Task</FormLabel><Input autoFocus value={title} onChange={event => setTitle(event.target.value)} slotProps={{ input: { maxLength: 255 } }} /></FormControl>
          <FormControl><FormLabel>Assigned to</FormLabel><StaffPicker value={assignee} onChange={setAssignee} disabled={save.isPending} /></FormControl>
          <FormControl><FormLabel>Due date and time</FormLabel><Input type="datetime-local" value={due} onChange={event => setDue(event.target.value)} /><Typography level="body-xs" sx={{ mt: .5 }}>Your local time · Leave blank if there is no due date.</Typography></FormControl>
          <FormControl><FormLabel>Notes</FormLabel><Textarea minRows={3} value={notes} onChange={event => setNotes(event.target.value)} /></FormControl>
          <Stack direction="row" justifyContent="flex-end" spacing={1}><Button variant="plain" color="neutral" disabled={save.isPending} onClick={onClose}>Cancel</Button><Button type="submit" disabled={!title.trim()} loading={save.isPending}>{task ? 'Save task' : 'Create task'}</Button></Stack>
        </Stack>
      </form>
    </ModalDialog>
  </Modal>;
}
